import { makeRequest } from './makeRequest'
import {
    CollectionLookup,
    AggregateCollectionLookup,
    CollectionClassLookup,
    AggregateCollectionClassLookup,
} from '../State/CollectionLookup'
import SessionStore from '../State/SessionStore'
import _ from 'lodash'
import FetchStore from './FetchStore'
import { getQueryPermissionFilters } from '../State/Permissions/HasPermissions'
import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'

export default async ({
    collection,
    ids = [],
    fields = [],
    filters = [],
    groupBy = [],
    chain = [],
    sortBy = [],
    limit = null,
    subQueries = [],
    id = null,
    data = {},
} = {}) => {
    if (!collection) console.trace()
    let response = {}
    if (SessionStore.user) {
        chain = chain.map((c) => {
            return {
                ...c,
                filters: [
                    ...(c.filters ?? []),
                    ...getQueryPermissionFilters(
                        c.collection,
                        SessionStore.user
                    ),
                ],
            }
        })
        subQueries = subQueries.map((sq) => {
            return {
                ...sq,
                filters: [
                    ...(sq.filters ?? []),
                    ...getQueryPermissionFilters(
                        sq.collection,
                        SessionStore.user
                    ),
                ],
            }
        })
        response = [
            await makeRequest({
                path: `api/v1.5/fetch/${collection}`,
                method: 'post',
                data: {
                    collection,
                    fields: [...fields], //_.intersection(fields, fieldPermissions[level]),
                    filters: [
                        ...filters,
                        ...getQueryPermissionFilters(
                            collection,
                            SessionStore.user
                        ),
                    ],
                    groupBy: [...groupBy],
                    chain: [...chain],
                    sortBy: [...sortBy],
                    limit,
                    subQueries: [...subQueries],
                    ...data,
                },
            }),
        ]
        let models = {}
        response.forEach((r) => {
            Object.entries(r.data).forEach(([rCollection, modelsData]) => {
                if (rCollection === 'labels') return
                rCollection = r.data.labels?.[rCollection] || rCollection
                try {
                    let model
                    if (!(groupBy.length && collection === rCollection)) {
                        model = CollectionLookup[rCollection].addMany(
                            modelsData,
                            {
                                trackUpdates: false,
                            }
                        )
                        models[rCollection] ??= new CollectionClassLookup[
                            rCollection
                        ]()
                        models[rCollection].addMany(modelsData, {
                            trackUpdates: false,
                        })
                    } else {
                        model = AggregateCollectionLookup[rCollection].addMany(
                            groupBy,
                            modelsData,
                            {
                                trackUpdates: false,
                            }
                        )
                        models[rCollection] ??=
                            new AggregateCollectionClassLookup[rCollection]()
                        models[rCollection].addMany(modelsData, {
                            trackUpdates: false,
                        })
                    }
                } catch (e) {
                    Sentry.captureException(e)
                    posthog.captureException(e)
                    console.error(e)
                    throw e
                }
            })
        })
        if (id) {
            FetchStore.setResponse(id, models)
        }
        return models
    } else {
        response = await makeRequest({
            path: `api/v1.5/fetch/${collection}`,
            method: 'post',
            data: {
                collection,
                ids: [...ids],
                fields: [...fields],
                filters: [...filters],
                groupBy: [...groupBy],
                chain: [...chain],
                sortBy: [...sortBy],
                limit,
                subQueries: [...subQueries],
            },
        })
        let models = {}
        Object.entries(response.data).forEach(([rCollection, modelsData]) => {
            if (rCollection === 'labels') return
            rCollection = response.data.labels?.[rCollection] || rCollection
            models[rCollection] ??= []
            try {
                let model
                if (!(groupBy.length && collection === rCollection)) {
                    model = CollectionLookup[rCollection].addMany(modelsData, {
                        trackUpdates: false,
                    })
                } else {
                    model = AggregateCollectionLookup[rCollection].addMany(
                        groupBy,
                        modelsData,
                        {
                            trackUpdates: false,
                        }
                    )
                }
                models[rCollection].push(...model)
            } catch (e) {
                Sentry.captureException(e)
                posthog.captureException(e)
                console.error(e)
                throw e
            }
        })
        if (id) {
            FetchStore.setResponse(id, models)
        }
        return models
    }
}

// New function to process data from the tRPC endpoint
export const processTRPCData = (data, id = null) => {
    if (!data) {
        console.error('processTRPCData received null or undefined data')
        return {}
    }

    const models = {}
    let returnRawData = true

    Object.entries(data).forEach(([collectionName, modelsData]) => {
        if (!modelsData || collectionName === 'labels') {
            return
        }

        // Determine the correct collection name (handling potential aliases if needed in future)
        const rCollection = collectionName // Simple mapping for now

        if (
            !CollectionLookup[rCollection] &&
            !AggregateCollectionLookup[rCollection]
        ) {
            return // Skip if no corresponding store
        }

        try {
            // Check if this collection should be treated as aggregated (based on name or structure if needed)
            // For now, assuming all data from getAppData is non-aggregated
            const isAggregated = false // Adjust if getAppData returns aggregated formats

            if (!isAggregated) {
                if (CollectionLookup[rCollection]) {
                    CollectionLookup[rCollection].addMany(modelsData, {
                        trackUpdates: false,
                    })
                    models[rCollection] ??= new CollectionClassLookup[
                        rCollection
                    ]()
                    // Ensure modelsData is an array before calling addMany
                    const dataArray = Array.isArray(modelsData)
                        ? modelsData
                        : modelsData
                          ? [modelsData]
                          : []
                    models[rCollection].addMany(dataArray, {
                        trackUpdates: false,
                    })
                    returnRawData = false
                } else {
                    console.warn(
                        `CollectionLookup[${rCollection}] not found, but not aggregated.`
                    )
                }
            } else {
                // Handle aggregated data if necessary (requires groupBy info)
                // This part is complex as groupBy info isn't directly in getAppData response
                // Might need to refactor or avoid using this function for aggregated data initially
                console.warn(
                    `Processing aggregated data for ${rCollection} not fully implemented in processTRPCData yet.`
                )
                // Example structure (needs groupBy argument):
                // if (AggregateCollectionLookup[rCollection]) {
                //     AggregateCollectionLookup[rCollection].addMany(groupBy, modelsData, {
                //         trackUpdates: false,
                //     });
                //     models[rCollection] ??= new AggregateCollectionClassLookup[rCollection]();
                //     models[rCollection].addMany(modelsData, { trackUpdates: false });
                // }
            }
        } catch (e) {
            Sentry.captureException(e)
            posthog.captureException(e)
            console.error(
                `Error processing collection ${rCollection} in processTRPCData:`,
                e
            )
            // Decide if we should throw or just log and continue
        }
    })

    // Optionally, could set response in FetchStore if needed, but maybe not necessary
    if (id) {
        FetchStore.setResponse(id, returnRawData ? data : {
            ...(data || {}),
            ...models,
        })
    }

    return returnRawData ? data : {
            ...(data || {}),
            ...models,
        }
}

function timeout(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
}
